'use client'

import { defaultBrowseSortFilterValue, defaultSearchSortFilterValue } from '@/lib/filters/filters'
import type { Filter } from '@/lib/filters/filters'
import { useFiltersStore } from '@/lib/store/filters-store'
import { useAppStore } from '@/lib/store/store'
import { cn } from '@/lib/utils'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import styles from '@/components/assets-explorer/filters/filter-sidebar.module.scss'
import { Button } from '@/components/ui/button'
import { FaChevronLeft } from 'react-icons/fa6'
import { PiSlidersHorizontalBold } from 'react-icons/pi'
import SortFilter from './sort-filter'
import { PremiumFilter } from './premium-filter'
import { Separator } from '@/components/ui/separator'
import { SimpleSearchBar } from '@/components/ui/simple-search-bar'
import { startAssetsGridTransition } from '@/lib/hooks/use-assets-grid-transition'
import { FilterIcon, PauseIcon, PlayIcon, Volume2Icon, VolumeXIcon, XIcon } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { useSearchStore } from '@/lib/store/search-store'
import { features } from '@/lib/config'
import useFilterSummary from '@/components/assets-explorer/filters/use-filter-summary'
import useAutoVideoPreviewsAllowed from '@/lib/hooks/use-auto-video-previews'

export type FilterSidebarRefinementEnabled =
  | boolean
  | {
      search?: boolean
      tags?: boolean
    }

type FiltersProps = {
  disabled?: boolean
  className?: string
  defaultBroadness?: number
  presentation?: 'sidebar' | 'panel'
  refinementEnabled?: FilterSidebarRefinementEnabled
  forceOpenUntilHydrated?: boolean
  initialFilters?: Filter[]
  initialMainCategorySlug?: string
  initialKeywords?: string[]
  initialCategoryDescription?: {
    name: string
    description: string
  }
}

type PreviewControlButtonProps = {
  active: boolean
  activeIcon: React.ReactNode
  inactiveIcon: React.ReactNode
  activeLabel: string
  inactiveLabel: string
  ariaLabel: string
  onClick: () => void
}

const PreviewControlButton = ({
  active,
  activeIcon,
  inactiveIcon,
  activeLabel,
  inactiveLabel,
  ariaLabel,
  onClick,
}: PreviewControlButtonProps) => {
  const label = active ? activeLabel : inactiveLabel

  return (
    <button
      type="button"
      title={label}
      className={cn(styles.previewControlButton, {
        [styles.previewControlButtonActive]: active,
      })}
      aria-label={ariaLabel}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className={styles.previewControlIcon}>{active ? activeIcon : inactiveIcon}</span>
      <span className={cn('body_4', styles.previewControlLabel)}>{label}</span>
    </button>
  )
}

// TODO: Translate Tailwind to modular CSS
const FilterSidebar = ({
  className,
  disabled = false,
  presentation = 'sidebar',
  refinementEnabled = true,
  forceOpenUntilHydrated = false,
  initialFilters = [],
  initialMainCategorySlug,
  initialKeywords = [],
  initialCategoryDescription,
}: FiltersProps) => {
  const filtersVisible = useAppStore((state) => state.filtersVisible)
  const showFilters = useAppStore((state) => state.showFilters)
  const hideFilters = useAppStore((state) => state.hideFilters)
  const storedVideoPreviewPreference = useAppStore((state) => state.videoPreviewPreference)
  const videoPreviewPreference = forceOpenUntilHydrated ? 'auto' : storedVideoPreviewPreference
  const setVideoPreviewPreference = useAppStore((state) => state.setVideoPreviewPreference)
  const autoVideoPreviewsAllowed = useAutoVideoPreviewsAllowed()
  const storedEnableVfxPreviewSoundOnHover = useAppStore((state) => state.enableVfxPreviewSoundOnHover)
  const enableVfxPreviewSoundOnHover = forceOpenUntilHydrated ? false : storedEnableVfxPreviewSoundOnHover
  const setEnableVfxPreviewSoundOnHover = useAppStore((state) => state.setEnableVfxPreviewSoundOnHover)
  const isPanel = presentation === 'panel'
  const opened = useMemo(
    () => (isPanel || forceOpenUntilHydrated || filtersVisible) && !disabled,
    [disabled, filtersVisible, forceOpenUntilHydrated, isPanel]
  )
  const params = useFiltersStore(useShallow((state) => state.params))
  const searchMainCategorySlug = useSearchStore((state) => state.searchMainCategorySlug)
  const storedFilters = useFiltersStore(useShallow((state) => state.filters))
  const filters = storedFilters.length ? storedFilters : initialFilters
  const setFilterValue = useFiltersStore((state) => state.setFilterValue)
  const clearFilters = useFiltersStore((state) => state.clearFilters)
  const search = useFiltersStore((state) => state.refinementQuery)
  const setSearch = useFiltersStore((state) => state.setRefinementQuery)
  const storedAvailableKeywords = useFiltersStore(useShallow((state) => state.availableKeywords))
  const availableKeywords = storedAvailableKeywords.length ? storedAvailableKeywords : initialKeywords
  const appliedKeywords = useFiltersStore(useShallow((state) => state.appliedKeywords))
  const setAppliedKeywords = useFiltersStore((state) => state.setAppliedKeywords)
  const toggleKeyword = useFiltersStore((state) => state.toggleKeyword)
  const clearRefinement = useFiltersStore((state) => state.clearRefinement)
  const searchBarRef = useRef<{ clear: () => void }>(null)

  const { mainCategory: mainCategorySlug, category: categorySlug, subCategory: subCategorySlug } = params
  const resolvedMainCategorySlug = mainCategorySlug ?? searchMainCategorySlug ?? initialMainCategorySlug
  const isVfxContext = resolvedMainCategorySlug === 'vfx'
  const showVfxPreviewToggles = isVfxContext && features.vfxEnhancedPreviews
  const videoPreviewsEnabled =
    videoPreviewPreference === 'on' || (videoPreviewPreference === 'auto' && autoVideoPreviewsAllowed)

  // Get the Content data to set the default broadness
  // const { data: content } = useQuery({
  //   enabled: !!mainCategorySlug && !!categorySlug && !!subCategorySlug && !!contentSlug,
  //   queryKey: ['contents', mainCategorySlug, categorySlug, subCategorySlug, contentSlug],
  //   queryFn: async () => {
  //     const { data } = await client.get<Content>(`/contents/${contentSlug}`, {
  //       params: {
  //         main_category: mainCategorySlug,
  //         category: categorySlug,
  //         sub_category: subCategorySlug,
  //       },
  //     })
  //     return data
  //   },
  // })

  const {
    appliedFiltersCount,
    hasFiltersApplied,
    isSearchRefinementEnabled,
    isSomeRefinementEnabled,
    isTagsRefinementEnabled,
  } = useFilterSummary(refinementEnabled)

  const toggle = useCallback(() => {
    if (opened) {
      hideFilters()
    } else {
      showFilters()
    }
  }, [hideFilters, opened, showFilters])

  // Sync initial values with storage
  useEffect(() => {
    if (isPanel) {
      return
    }

    if (opened) {
      showFilters({
        shouldPersist: false,
      })
    } else {
      hideFilters({
        shouldPersist: false,
      })
    }
  }, [hideFilters, isPanel, opened, showFilters])

  // Determine if we're in search mode by default, that is, when we only know the main category
  const isSearchMode = useMemo(() => {
    return !categorySlug || !subCategorySlug
  }, [categorySlug, subCategorySlug])

  // Determine teh default Sort value
  const defaultSortValue = useMemo(() => {
    return isSearchMode ? defaultSearchSortFilterValue : defaultBrowseSortFilterValue
  }, [isSearchMode])

  // Determine the defaultBroadness value
  // const defaultBroadnessValue: BroadnessFilterValue = useMemo(() => {
  //   if (content?.searchBroadness) {
  //     return content.searchBroadness
  //   }
  //
  //   return isSearchMode ? defaultSearchBroadnessFilterValue : defaultBrowseBroadnessFilterValue
  // }, [content?.searchBroadness, isSearchMode])

  const resetAllFilters = useCallback(() => {
    clearFilters({
      onlyEnabled: true,
    })
    clearRefinement()
    searchBarRef.current?.clear()
  }, [clearFilters, clearRefinement])

  return (
    <motion.div
      data-slot="filter-sidebar"
      className={cn(styles.main, 'tw-h-full', className)}
      initial={{
        minWidth: isPanel ? '100%' : opened ? 'var(--filters-opened-width)' : 'var(--filters-closed-width)',
      }}
      animate={{
        minWidth: isPanel ? '100%' : opened ? 'var(--filters-opened-width)' : 'var(--filters-closed-width)',
      }}
    >
      {/* Header */}
      <div
        className={cn(styles.header, {
          [styles.headerOpened]: opened,
        })}
      >
        {opened && (
          <>
            <div className={styles.titleRow}>
              {hasFiltersApplied && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="!tw-text-[var(--filter-sidebar-color)]"
                  onClick={() => resetAllFilters()}
                >
                  Reset all
                </Button>
              )}
            </div>
            {!isPanel && (
              <Button
                type="button"
                title="Hide filters"
                size="icon"
                variant="ghost"
                shape="rounded"
                onClick={() => toggle()}
              >
                <FaChevronLeft className={styles.filtersTrigger} />
              </Button>
            )}
          </>
        )}
        {!opened && !isPanel && (
          <Button
            className="tw-relative"
            type="button"
            title="Show filters"
            size="icon"
            variant="ghost"
            shape="rounded"
            disabled={disabled}
            onClick={() => toggle()}
          >
            {!!appliedFiltersCount && (
              <span className="tw-absolute -tw-right-1 -tw-top-1 tw-w-5 tw-rounded-full tw-bg-[#F36C17] tw-py-1 tw-text-xs tw-leading-none tw-text-white">
                {appliedFiltersCount}
              </span>
            )}
            <PiSlidersHorizontalBold className={styles.filtersTrigger} />
          </Button>
        )}
      </div>

      {/* Body */}
      <motion.div
        animate={{
          opacity: opened ? 1 : 0,
          display: opened ? 'block' : 'none',
        }}
      >
        {opened && (
          <div className="tw-flex tw-flex-col tw-py-2">
            <div className="tw-flex tw-flex-col tw-space-y-4">
              {filters.map(({ __typename, name, value, enabled, visible }) => {
                return (
                  <div key={`${name}-filter`}>
                    {__typename === 'SortFilter' && visible && (
                      <SortFilter
                        triggerClassName="tw-text-[var(--filter-sidebar-color)]"
                        contentClassName="!tw-text-[var(--filter-sidebar-color)]"
                        value={value ?? defaultSortValue}
                        enabled={enabled}
                        onChange={(value) => {
                          setFilterValue(name, value)
                        }}
                      />
                    )}
                    {__typename === 'PremiumFilter' && visible && (
                      <PremiumFilter
                        value={value}
                        enabled={enabled}
                        onChange={(value) => {
                          setFilterValue(name, value)
                        }}
                      />
                    )}
                    {/*{__typename === 'ResolutionFilter' && visible && (
                      <ResolutionFilter
                        value={value}
                        onChange={(value) => {
                          setFilterValue(name, value)
                        }}
                      />
                    )}
                    {__typename === 'BroadnessFilter' && visible && (
                      <div>
                        <div className="body_3 tw-mb-2 tw-text-[var(--filter-sidebar-color)]">{label}</div>
                        {isLoadingContent && <Skeleton className="tw-h-6 tw-w-16" />}
                        {!isLoadingContent && (
                          <BroadnessFilter
                            value={value ?? defaultBroadnessValue}
                            onChange={(value) => {
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                      </div>
                    )}*/}
                  </div>
                )
              })}
            </div>
            {/* Local refinement */}
            {isSomeRefinementEnabled && (
              <>
                <Separator className="tw-my-4 !tw-bg-[var(--Primary-400)]" />

                <div className="tw-flex tw-flex-col tw-space-y-2">
                  {isSearchRefinementEnabled && (
                    <SimpleSearchBar
                      ref={searchBarRef}
                      placeholder="Filter results"
                      icon={<FilterIcon className="tw-ml-1 tw-h-6 tw-w-6 tw-text-[var(--Primary-200)] tw-opacity-50" />}
                      defaultValue={search ?? ''}
                      onValueChange={(search) => {
                        // Reset search if empty
                        console.debug('Search refinement changed', { search })
                        startAssetsGridTransition()
                        setSearch(search.trim() || undefined)
                      }}
                    />
                  )}

                  {/* Display as a carousel */}

                  {/*<div className="tw-flex tw-max-w-[224px] tw-flex-nowrap tw-items-center">*/}
                  {/*  <div className="-tw-mr-6 tw-w-min tw-max-w-full tw-pr-6">*/}
                  {/*    <Carousel*/}
                  {/*      opts={{*/}
                  {/*        align: 'start',*/}
                  {/*      }}*/}
                  {/*      plugins={[*/}
                  {/*        WheelGesturesPlugin({*/}
                  {/*          forceWheelAxis: 'y',*/}
                  {/*        }),*/}
                  {/*      ]}*/}
                  {/*    >*/}
                  {/*      <CarouselContent className="!tw--ml-2">*/}
                  {/*        {React.Children.toArray(*/}
                  {/*          availableKeywords.map((keyword) => (*/}
                  {/*            <CarouselItem className="tw-max-w-min !tw-pl-2 tw-duration-500 tw-animate-in tw-fade-in">*/}
                  {/*              <Button*/}
                  {/*                type="button"*/}
                  {/*                variant="ghost"*/}
                  {/*                className={cn(*/}
                  {/*                  '!tw-items-center !tw-rounded-full !tw-text-xs !tw-font-light tw-capitalize !tw-text-[var(--Primary-100)] hover:tw-bg-[var(--Primary-300)]',*/}
                  {/*                  {*/}
                  {/*                    '!tw-bg-[var(--Primary-300)] !tw-text-[var(--Primary)]':*/}
                  {/*                      appliedKeywords.includes(keyword),*/}
                  {/*                  }*/}
                  {/*                )}*/}
                  {/*                onClick={() => {*/}
                  {/*                  setAppliedKeywords((prevState) => {*/}
                  {/*                    if (prevState.includes(keyword)) {*/}
                  {/*                      return prevState.filter((kw) => kw !== keyword)*/}
                  {/*                    }*/}
                  {/*                    return [...prevState, keyword]*/}
                  {/*                  })*/}
                  {/*                }}*/}
                  {/*              >*/}
                  {/*                {keyword}*/}
                  {/*                {appliedKeywords.includes(keyword) && (*/}
                  {/*                  <X className="tw-relative tw-left-1 tw-h-4 tw-w-4 tw-opacity-50" />*/}
                  {/*                )}*/}
                  {/*              </Button>*/}
                  {/*            </CarouselItem>*/}
                  {/*          ))*/}
                  {/*        )}*/}
                  {/*      </CarouselContent>*/}
                  {/*    </Carousel>*/}
                  {/*  </div>*/}

                  {/*  /!*"Clear all" button*!/*/}
                  {/*  <div>*/}
                  {/*    {!!availableKeywords.length && (*/}
                  {/*      <Button*/}
                  {/*        name="Clear all filters"*/}
                  {/*        type="button"*/}
                  {/*        variant="transparent"*/}
                  {/*        size="icon"*/}
                  {/*        onClick={() => {*/}
                  {/*          setAppliedKeywords([])*/}
                  {/*        }}*/}
                  {/*        disabled={!appliedKeywords.length}*/}
                  {/*      >*/}
                  {/*        <X className="tw-h-4 tw-w-4" />*/}
                  {/*      </Button>*/}
                  {/*    )}*/}
                  {/*  </div>*/}
                  {/*</div>*/}

                  {/* Display as a flex list */}
                  {isTagsRefinementEnabled && (
                    <div className="tw-flex tw-w-full tw-max-w-[224px] tw-flex-wrap tw-items-center tw-gap-2">
                      {availableKeywords.map((keyword) => (
                        <Button
                          key={keyword}
                          type="button"
                          variant="ghost"
                          className={cn(
                            '!tw-items-center !tw-rounded-full !tw-text-xs !tw-font-light tw-capitalize hover:tw-bg-[var(--Primary-300)] hover:!tw-text-[var(--Primary-100)]',
                            {
                              '!tw-text-[var(--asset-keyword-color)]': !appliedKeywords.includes(keyword),
                              '!tw-bg-[var(--Primary-300)] !tw-text-[var(--Primary-100)]':
                                appliedKeywords.includes(keyword),
                            }
                          )}
                          onClick={() => {
                            toggleKeyword(keyword)
                          }}
                        >
                          {keyword}
                          {appliedKeywords.includes(keyword) && (
                            <XIcon className="tw-relative tw-left-1 tw-h-4 tw-w-4 tw-opacity-50" />
                          )}
                        </Button>
                      ))}
                      {!!availableKeywords.length && (
                        <Button
                          key={'clear-all'}
                          title="Clear all filters"
                          type="button"
                          variant="transparent"
                          size="icon"
                          onClick={() => {
                            setAppliedKeywords([])
                          }}
                          disabled={!appliedKeywords.length}
                        >
                          <XIcon className="tw-h-4 tw-w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {showVfxPreviewToggles && (
              <>
                <Separator className="tw-my-4 !tw-bg-[var(--Primary-400)]" />
                <div className={styles.previewControls}>
                  <PreviewControlButton
                    active={videoPreviewsEnabled}
                    activeIcon={<PauseIcon className="tw-h-5 tw-w-5" />}
                    inactiveIcon={<PlayIcon className="tw-h-5 tw-w-5" />}
                    activeLabel={videoPreviewPreference === 'auto' ? 'Previews Auto' : 'Previews On'}
                    inactiveLabel="Previews Off"
                    ariaLabel="Cycle animated preview mode in the VFX asset grid"
                    onClick={() => {
                      if (videoPreviewPreference === 'auto') {
                        setVideoPreviewPreference('on')
                        return
                      }

                      if (videoPreviewPreference === 'on') {
                        setVideoPreviewPreference('off')
                        return
                      }

                      setVideoPreviewPreference('auto')
                    }}
                  />
                  <PreviewControlButton
                    active={enableVfxPreviewSoundOnHover}
                    activeIcon={<Volume2Icon className="tw-h-5 tw-w-5" />}
                    inactiveIcon={<VolumeXIcon className="tw-h-5 tw-w-5" />}
                    activeLabel="Sounds On"
                    inactiveLabel="Sounds Off"
                    ariaLabel="Toggle preview sound on hover for VFX assets"
                    onClick={() => setEnableVfxPreviewSoundOnHover(!enableVfxPreviewSoundOnHover)}
                  />
                </div>
              </>
            )}

            {initialCategoryDescription && (
              <section
                className={styles.categoryDescription}
                aria-label={`Description for ${initialCategoryDescription.name}`}
              >
                <Separator className="tw-my-4 !tw-bg-[var(--Primary-400)]" />
                <p className={styles.categoryDescriptionName}>
                  {initialCategoryDescription.name}
                </p>
                <div
                  className={styles.categoryDescriptionContent}
                  dangerouslySetInnerHTML={{ __html: initialCategoryDescription.description }}
                />
              </section>
            )}

            {/*<Accordion type="multiple" className="w-full" defaultValue={appliedFilterNames}>
              {React.Children.toArray(
                filters.map(({ __typename, label, name, value, ...props }) => {
                  return (
                    <AccordionItem value={name}>
                      <AccordionTrigger className="!tw-p-2">
                        <div className={styles.filterHeader}>
                          <div className="body_3">{label}</div>
                          {!!value && (
                            <div
                              role="button"
                              name={`Clear ${label}`}
                              className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                              onClick={(e) => {
                                e.preventDefault()
                                setFilterValue(name, undefined)
                              }}
                            >
                              Clear
                            </div>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="!tw-px-2 !tw-pb-2">
                        {__typename === 'SortFilter' && (
                          <SortFilter
                            value={value}
                            onChange={(value) => {
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                        {__typename === 'PremiumFilter' && (
                          <PremiumFilter
                            value={value}
                            onChange={(value) => {
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                        {__typename === 'ResolutionFilter' && (
                          <ResolutionFilter
                            options={'options' in props ? props.options : []}
                            value={value}
                            onChange={(value) => {
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                        {__typename === 'DurationFilter' && (
                          <DurationFilter
                            min={(props as Pick<DurationFilterType, 'min'>).min ?? 0}
                            max={(props as Pick<DurationFilterType, 'max'>).max ?? 0}
                            value={value}
                            onChange={(value) => {
                              setFilterValue(name, undefined)
                            }}
                          />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )
                })
              )}
            </Accordion>*/}
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

export { FilterSidebar }
export type { FiltersProps }
